import React, { useState } from 'react';
import { User } from '@supabase/supabase-js';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Heart, 
  Eye, 
  MessageSquare, 
  GitFork, 
  Share2, 
  Download, 
  Calendar, 
  Code, 
  Zap
} from "lucide-react";
import { ShowcaseItem, ShowcaseComment } from '@/types/showcase';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { SecondMeConfig } from '@/components/second-me/types';

interface AgentDetailProps {
  item: ShowcaseItem;
  config: SecondMeConfig;
  currentUser?: User | null;
  isLiked?: boolean;
  comments?: ShowcaseComment[];
  similarItems?: ShowcaseItem[];
  onLike?: () => void;
  onFork?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  className?: string;
}

/**
 * Detailed view component for a showcase item
 */
export const AgentDetail: React.FC<AgentDetailProps> = ({
  item,
  config,
  currentUser,
  isLiked = false,
  comments = [],
  similarItems = [],
  onLike,
  onFork,
  onShare,
  onDownload,
  className,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Format creator name if available
  const creatorName = item.metadata?.creatorName || 'Anonymous';
  
  // Get complexity color
  const getComplexityColor = (level?: string) => {
    switch(level) {
      case 'beginner': return 'bg-green-100 text-green-800 border-green-300';
      case 'intermediate': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'advanced': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className={cn("max-w-4xl mx-auto", className)}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <div className="flex-shrink-0">
          <Avatar className="h-24 w-24 border">
            <AvatarImage src={item.thumbnailUrl || '/placeholder.svg'} alt={item.title} />
            <AvatarFallback className="text-2xl">
              {item.title.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
        
        <div className="flex-grow">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold">{item.title}</h1>
              <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                <span>By {creatorName}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {item.createdAt 
                    ? formatDistanceToNow(item.createdAt, { addSuffix: true }) 
                    : 'Recently'}
                </span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {item.featured && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">
                  Featured
                </Badge>
              )}
              
              {item.metadata?.complexity && (
                <Badge 
                  variant="outline" 
                  className={getComplexityColor(item.metadata.complexity)}
                >
                  {item.metadata.complexity}
                </Badge>
              )}
            </div>
          </div>
          
          <p className="text-muted-foreground mb-4">{item.description}</p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {item.tags.map((tag, index) => (
              <Badge key={`tag-${index}`} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-1">
              <Eye size={16} className="text-muted-foreground" />
              <span>{item.views || 0} views</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Heart 
                size={16} 
                className={isLiked ? "text-red-500" : "text-muted-foreground"}
                fill={isLiked ? "currentColor" : "none"}
              />
              <span>{item.likes || 0} likes</span>
            </div>
            
            <div className="flex items-center gap-1">
              <MessageSquare size={16} className="text-muted-foreground" />
              <span>{comments.length} comments</span>
            </div>
            
            <div className="flex items-center gap-1">
              <GitFork size={16} className="text-muted-foreground" />
              <span>{item.metadata?.forkCount || 0} forks</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Button 
          className={cn(
            isLiked ? "bg-red-100 text-red-800 hover:bg-red-200" : ""
          )}
          variant={isLiked ? "outline" : "default"}
          onClick={onLike}
          disabled={!currentUser}
        >
          <Heart 
            size={16} 
            className="mr-2" 
            fill={isLiked ? "currentColor" : "none"} 
          />
          {isLiked ? "Liked" : "Like"}
        </Button>
        
        <Button variant="outline" onClick={onFork} disabled={!currentUser}>
          <GitFork size={16} className="mr-2" />
          Fork
        </Button>
        
        <Button variant="outline" onClick={onShare}>
          <Share2 size={16} className="mr-2" />
          Share
        </Button>
        
        <Button variant="outline" onClick={onDownload}>
          <Download size={16} className="mr-2" />
          Download
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs 
        defaultValue="overview" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="mb-8"
      >
        <TabsList className="grid grid-cols-4 sm:grid-cols-5 mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="comments" className="hidden sm:block">Comments</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Zap size={18} className="mr-2 text-amber-500" />
                  Personality Traits
                </h3>
                
                {config.personalityTraits && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Openness</span>
                        <span>{config.personalityTraits.openness}/100</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${config.personalityTraits.openness}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Conscientiousness</span>
                        <span>{config.personalityTraits.conscientiousness}/100</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${config.personalityTraits.conscientiousness}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Extraversion</span>
                        <span>{config.personalityTraits.extraversion}/100</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${config.personalityTraits.extraversion}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Agreeableness</span>
                        <span>{config.personalityTraits.agreeableness}/100</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${config.personalityTraits.agreeableness}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Neuroticism</span>
                        <span>{config.personalityTraits.neuroticism}/100</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${config.personalityTraits.neuroticism}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Code size={18} className="mr-2 text-blue-500" />
                  Learning Style
                </h3>
                
                {config.learningStyle && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Visual</span>
                        <span>Verbal</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-indigo-500 h-2 rounded-full" 
                          style={{ width: `${config.learningStyle.visualVerbal}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Sequential</span>
                        <span>Global</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-cyan-500 h-2 rounded-full" 
                          style={{ width: `${config.learningStyle.sequentialGlobal}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Active</span>
                        <span>Reflective</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${config.learningStyle.activeReflective}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Sensing</span>
                        <span>Intuitive</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-teal-500 h-2 rounded-full" 
                          style={{ width: `${config.learningStyle.sensingIntuitive}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="mt-4 text-sm text-muted-foreground">
                      {config.learningStyle.description}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Core Values */}
            {config.coreValues && config.coreValues.length > 0 && (
              <Card className="md:col-span-2">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Core Values</h3>
                  
                  <div className="space-y-4">
                    {config.coreValues.map((value, index) => (
                      <div key={`value-${index}`}>
                        <h4 className="font-medium">{value.name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{value.description}</p>
                        <div className="bg-muted p-3 rounded text-sm italic">
                          "{value.example}"
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Capabilities Tab */}
        <TabsContent value="capabilities" className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h3 className="text-lg font-semibold">Expertise Areas</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {config.expertiseAreas && config.expertiseAreas.map((area, index) => (
                  <div key={`area-${index}`} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">{area.name}</h4>
                      <Badge>{area.level}</Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {area.concepts && area.concepts.map((concept, idx) => (
                        <Badge key={`concept-${idx}`} variant="outline" className="text-xs">
                          {concept}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Agent Configuration</h3>
              
              <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
                {JSON.stringify(config, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Usage Examples</h3>
              
              {Array.isArray(item.metadata?.exampleInputs) && Array.isArray(item.metadata?.exampleOutputs) && 
               item.metadata.exampleInputs.length > 0 && (
                <div className="space-y-6">
                  {item.metadata.exampleInputs.map((input, index) => {
                    const output = item.metadata?.exampleOutputs?.[index] || "No response available";
                    return (
                      <div key={`example-${index}`} className="border rounded-lg overflow-hidden">
                        <div className="bg-muted p-3 border-b">
                          <span className="font-medium">Example {index + 1}</span>
                        </div>
                        <div className="p-4 border-b">
                          <h4 className="text-sm font-medium mb-2">Input:</h4>
                          <div className="bg-background p-3 rounded">
                            {input}
                          </div>
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-medium mb-2">Response:</h4>
                          <div className="bg-background p-3 rounded">
                            {output}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {(!Array.isArray(item.metadata?.exampleInputs) || item.metadata?.exampleInputs.length === 0) && (
                <p className="text-muted-foreground">No usage examples available for this agent.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Comments ({comments.length})</h3>
              
              {comments.length > 0 ? (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {comment.userId.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-grow">
                          <div className="flex justify-between">
                            <span className="font-medium">
                              {/* We would typically fetch the user name */}
                              User {comment.userId.substring(0, 6)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(comment.createdAt!, { addSuffix: true })}
                            </span>
                          </div>
                          
                          <p className="mt-1">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No comments yet. Be the first to comment!</p>
              )}
              
              {/* Comment input - only show if user is logged in */}
              {currentUser && (
                <div className="mt-6">
                  <textarea 
                    className="w-full border rounded-lg p-3 resize-none"
                    rows={3}
                    placeholder="Write a comment..."
                  />
                  <Button className="mt-2">Post Comment</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Similar Agents */}
      {similarItems.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold mb-4">Similar Agents</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {similarItems.map((similar) => (
              <div key={similar.id} className="border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={similar.thumbnailUrl || '/placeholder.svg'} alt={similar.title} />
                    <AvatarFallback>
                      {similar.title.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div>
                    <h3 className="font-medium line-clamp-1">{similar.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {similar.description}
                    </p>
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Eye size={12} />
                        <span>{similar.views}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart size={12} />
                        <span>{similar.likes}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};